import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import PenIcon from 'assets/icons/pen.svg';
import TrashIcon from 'assets/icons/trash.svg';
import AttributeService from 'services/AttributeService';
import UserService from 'services/UserService';
import NonconformityReasonService, {
  INonconformityReason,
  INonconformityReasonPayload,
} from 'services/NonconformityReasonService';
import { formatDate, isAccountActive, normalizeTurkish, openNotification } from 'utils';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

type ScopeFilter = 'all' | 'general' | 'private';
type DateSortField = 'createdAt' | 'updatedAt';
type TableSortOrder = 'ascend' | 'descend' | null;

type AttributeOption = {
  id: number;
  name: string;
};

type AccountOption = {
  id: number;
  name: string;
  email?: string;
};

const ALLOWED_ACCOUNT_ROLES = ['ADMIN', 'INSPECTOR'];

const isActiveAttribute = (item: any) => item?.isActive !== false && item?.active !== false;

const extractAttributeOptions = (payload: any): AttributeOption[] => {
  const resolved = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
  const result: AttributeOption[] = [];
  const visit = (items: any[]) => {
    items.forEach((item) => {
      const id = Number(item?.id);
      const isGroup = Boolean(item?.isGroup);
      const name = String(item?.name ?? item?.description ?? '').trim();
      if (Number.isFinite(id) && id > 0 && !isGroup) {
        if (!result.some((existing) => existing.id === id)) {
          result.push({ id, name: name || `#${id}` });
        }
      }
      if (Array.isArray(item?.children)) {
        visit(item.children);
      }
    });
  };
  visit(resolved);
  return result.sort((a, b) => a.name.localeCompare(b.name, 'tr'));
};

const extractActiveAttributeOptions = (payload: any): AttributeOption[] => {
  const resolved = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
  const result: AttributeOption[] = [];
  const visit = (items: any[]) => {
    items.forEach((item) => {
      if (Array.isArray(item?.children)) {
        visit(item.children);
      }
      if (!isActiveAttribute(item)) {
        return;
      }
      const id = Number(item?.id);
      const isGroup = Boolean(item?.isGroup);
      const name = String(item?.name ?? item?.description ?? '').trim();
      if (Number.isFinite(id) && id > 0 && !isGroup && !result.some((existing) => existing.id === id)) {
        result.push({ id, name: name || `#${id}` });
      }
    });
  };
  visit(resolved);
  return result.sort((a, b) => a.name.localeCompare(b.name, 'tr'));
};


const extractAccountOptions = (payload: any): AccountOption[] => {
  const resolved = Array.isArray(payload?.data?.content)
    ? payload.data.content
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload)
        ? payload
        : [];
  const result: AccountOption[] = [];
  resolved.forEach((item: any) => {
    const id = Number(item?.id);
    if (!Number.isFinite(id) || id <= 0) {
      return;
    }
    const roleNames = (item?.roles ?? []).map((role: any) => role?.name).filter(Boolean);
    const hasAllowedRole = roleNames.some((name: string) => ALLOWED_ACCOUNT_ROLES.includes(name));
    if (!hasAllowedRole) {
      return;
    }
    if (!isAccountActive(item)) {
      return;
    }
    const fullName = `${item?.firstName ?? ''} ${item?.lastName ?? ''}`.trim();
    const email = String(item?.email ?? '').trim();
    const name = fullName || email || `#${id}`;
    if (!result.some((existing) => existing.id === id)) {
      result.push({ id, name, email: email || undefined });
    }
  });
  return result.sort((a, b) => a.name.localeCompare(b.name, 'tr'));
};

const filterOptionByTurkishLabel = (input: string, option?: { label?: any }) =>
  normalizeTurkish(String(option?.label ?? '')).includes(normalizeTurkish(input ?? ''));

export const buildEditorAttributeOptions = (
  activeOptions: AttributeOption[],
  allOptions: AttributeOption[],
  editingItem?: Pick<INonconformityReason, 'attributeId' | 'attributeName'> | null,
): AttributeOption[] => {
  const merged = [...activeOptions];
  const selectedAttributeId = Number(editingItem?.attributeId);
  if (Number.isFinite(selectedAttributeId) && selectedAttributeId > 0) {
    const exists = merged.some((item) => item.id === selectedAttributeId);
    if (!exists) {
      const selectedAttributeName = String(editingItem?.attributeName ?? '').trim();
      const fallbackAttributeName = allOptions.find((item) => item.id === selectedAttributeId)?.name;
      merged.push({
        id: selectedAttributeId,
        name: selectedAttributeName || fallbackAttributeName || `Parametre #${selectedAttributeId}`,
      });
    }
  }
  return merged.sort((a, b) => a.name.localeCompare(b.name, 'tr'));
};

const NonconformityReasonList: React.FC = () => {
  const [form] = Form.useForm();
  const [filterForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<INonconformityReason[]>([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [editorDrawerVisible, setEditorDrawerVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<INonconformityReason | null>(null);
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');
  const [attributeFilterId, setAttributeFilterId] = useState<number | undefined>(undefined);
  const [accountFilterId, setAccountFilterId] = useState<number | undefined>(undefined);
  const [createdAtRangeFilter, setCreatedAtRangeFilter] = useState<[string, string] | undefined>(undefined);
  const [updatedAtRangeFilter, setUpdatedAtRangeFilter] = useState<[string, string] | undefined>(undefined);
  const [sortField, setSortField] = useState<DateSortField | null>(null);
  const [sortOrder, setSortOrder] = useState<TableSortOrder>(null);
  const [attributeOptions, setAttributeOptions] = useState<AttributeOption[]>([]);
  const [activeAttributeOptions, setActiveAttributeOptions] = useState<AttributeOption[]>([]);
  const [accountOptions, setAccountOptions] = useState<AccountOption[]>([]);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchText.trim());
    }, 400);
    return () => window.clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    if (pagination.current !== 1) {
      setPagination((prev) => ({ ...prev, current: 1 }));
    }
  }, [
    debouncedSearch,
    scopeFilter,
    attributeFilterId,
    accountFilterId,
    createdAtRangeFilter,
    updatedAtRangeFilter,
    sortField,
    sortOrder,
  ]);

  useEffect(() => {
    AttributeService.getAll()
      .then((response) => {
        setAttributeOptions(extractAttributeOptions(response));
        setActiveAttributeOptions(extractActiveAttributeOptions(response));
      })
      .catch((error) => {
        console.error('Attributes could not be loaded', error);
        setAttributeOptions([]);
        setActiveAttributeOptions([]);
      });
  }, []);

  useEffect(() => {
    UserService.getUsersWithoutPagination({})
      .then((response: any) => {
        setAccountOptions(extractAccountOptions(response));
      })
      .catch((error) => {
        console.error('Accounts could not be loaded', error);
        setAccountOptions([]);
      });
  }, []);

  const buildFilters = useCallback(() => {
    const resolvedSort =
      sortField && sortOrder
        ? `${sortField},${sortOrder === 'ascend' ? 'asc' : 'desc'}`
        : 'isGeneral,asc';
    const params: Record<string, any> = {
      page: pagination.current - 1,
      size: pagination.pageSize,
      sort: resolvedSort,
    };
    if (debouncedSearch) {
      params['search.contains'] = debouncedSearch;
    }
    if (scopeFilter === 'general') {
      params['isGeneral.equals'] = true;
    } else if (scopeFilter === 'private') {
      params['isGeneral.equals'] = false;
    }
    if (attributeFilterId !== undefined) {
      params['attributeId.equals'] = attributeFilterId;
    }
    if (accountFilterId !== undefined) {
      params['accountId.equals'] = accountFilterId;
    }
    if (createdAtRangeFilter) {
      params['createdAt.greaterThanOrEqual'] = createdAtRangeFilter[0];
      params['createdAt.lessThanOrEqual'] = createdAtRangeFilter[1];
    }
    if (updatedAtRangeFilter) {
      params['updatedAt.greaterThanOrEqual'] = updatedAtRangeFilter[0];
      params['updatedAt.lessThanOrEqual'] = updatedAtRangeFilter[1];
    }
    return params;
  }, [
    pagination.current,
    pagination.pageSize,
    debouncedSearch,
    scopeFilter,
    attributeFilterId,
    accountFilterId,
    createdAtRangeFilter,
    updatedAtRangeFilter,
    sortField,
    sortOrder,
  ]);

  const fetchList = useCallback(() => {
    setLoading(true);
    NonconformityReasonService.list(buildFilters())
      .then((response) => {
        setItems(response.data.content);
        setPagination((prev) => ({ ...prev, total: response.data.totalElements }));
      })
      .catch((error) => {
        console.error('Nonconformity reasons fetch failed', error);
        openNotification('error', 'Hata', 'Uygunsuzluk nedenleri listelenirken hata oluştu.');
      })
      .finally(() => setLoading(false));
  }, [buildFilters]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openCreateDrawer = () => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue({
      isGeneral: true,
      accountId: undefined,
      attributeId: undefined,
    });
    setEditorDrawerVisible(true);
  };

  const openEditDrawer = (record: INonconformityReason) => {
    setEditingItem(record);
    form.setFieldsValue({
      abbreviation: record.abbreviation,
      description: record.description,
      isGeneral: record.isGeneral,
      accountId: record.accountId ?? record.account?.id ?? undefined,
      attributeId: record.attributeId ?? undefined,
    });
    setEditorDrawerVisible(true);
  };

  const closeEditorDrawer = () => {
    setEditorDrawerVisible(false);
    setEditingItem(null);
    setSubmitLoading(false);
    form.resetFields();
  };

  const handleDelete = async (record: INonconformityReason) => {
    try {
      await NonconformityReasonService.remove(record.id);
      openNotification('success', 'Başarılı', 'Uygunsuzluk nedeni silindi.');
      fetchList();
    } catch (error) {
      console.error('Nonconformity reason delete failed', error);
      openNotification('error', 'Hata', 'Silme sırasında hata oluştu.');
    }
  };

  const handleSubmit = async (values: any) => {
    const payload: INonconformityReasonPayload = {
      abbreviation: String(values.abbreviation ?? '').trim(),
      description: String(values.description ?? '').trim(),
      isGeneral: values.isGeneral !== false,
      accountId: values.accountId !== undefined && values.accountId !== null ? Number(values.accountId) : null,
      attributeId: values.attributeId !== undefined && values.attributeId !== null ? Number(values.attributeId) : null,
    };

    if (!payload.abbreviation) {
      openNotification('error', 'Hata', 'Kısaltma zorunludur.');
      return;
    }
    if (!payload.description) {
      openNotification('error', 'Hata', 'Açıklama zorunludur.');
      return;
    }
    if (!payload.attributeId) {
      openNotification('error', 'Hata', 'Parametre seçiniz.');
      return;
    }

    setSubmitLoading(true);
    try {
      if (editingItem) {
        await NonconformityReasonService.update(editingItem.id, payload);
        openNotification('success', 'Başarılı', 'Uygunsuzluk nedeni güncellendi.');
      } else {
        await NonconformityReasonService.create(payload);
        openNotification('success', 'Başarılı', 'Uygunsuzluk nedeni oluşturuldu.');
      }
      closeEditorDrawer();
      fetchList();
    } catch (error: any) {
      console.error('Nonconformity reason save failed', error);
      const message = error?.response?.data?.message || 'Kaydetme sırasında hata oluştu.';
      openNotification('error', 'Hata', message);
      setSubmitLoading(false);
    }
  };

  const handleTableChange = (nextPagination: any, _filters: any, sorter: any) => {
    const resolvedSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextField =
      resolvedSorter?.field === 'createdAt' || resolvedSorter?.field === 'updatedAt'
        ? (resolvedSorter.field as DateSortField)
        : null;
    const nextOrder = (resolvedSorter?.order ?? null) as TableSortOrder;
    setSortField(nextField);
    setSortOrder(nextField ? nextOrder : null);
    setPagination((prev) => ({
      ...prev,
      current: nextPagination.current ?? prev.current,
      pageSize: nextPagination.pageSize ?? prev.pageSize,
    }));
  };

  const accountLabelById = useMemo(() => {
    const map = new Map<number, string>();
    accountOptions.forEach((account) => {
      map.set(account.id, account.name);
    });
    return map;
  }, [accountOptions]);

  const userSelectOptions = useMemo(
    () =>
      accountOptions.map((item) => ({
        value: item.id,
        label: item.email ? `${item.name} (${item.email})` : item.name,
      })),
    [accountOptions],
  );

  const editorUserSelectOptions = useMemo(() => {
    const merged = [...accountOptions];
    const selectedAccountId = Number(editingItem?.accountId ?? editingItem?.account?.id);
    if (!Number.isNaN(selectedAccountId) && selectedAccountId > 0) {
      const exists = merged.some((item) => item.id === selectedAccountId);
      if (!exists) {
        const firstName = editingItem?.account?.firstName ?? '';
        const lastName = editingItem?.account?.lastName ?? '';
        const email = editingItem?.account?.email ?? '';
        const name = `${firstName} ${lastName}`.trim() || email || `#${selectedAccountId}`;
        merged.push({
          id: selectedAccountId,
          name,
          email: email || undefined,
        });
      }
    }
    return merged
      .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
      .map((item) => ({
        value: item.id,
        label: item.email ? `${item.name} (${item.email})` : item.name,
      }));
  }, [accountOptions, editingItem]);

  const editorAttributeSelectOptions = useMemo(
    () => buildEditorAttributeOptions(activeAttributeOptions, attributeOptions, editingItem),
    [activeAttributeOptions, attributeOptions, editingItem],
  );

  const columns = useMemo(
    () => [
      {
        title: 'Parametre',
        dataIndex: 'attributeName',
        key: 'attributeName',
        render: (_: any, record: INonconformityReason) => record.attributeName || '-',
      },
      {
        title: 'Kısaltma',
        dataIndex: 'abbreviation',
        key: 'abbreviation',
      },
      {
        title: 'Açıklama',
        dataIndex: 'description',
        key: 'description',
      },
      {
        title: 'Kapsam',
        dataIndex: 'isGeneral',
        key: 'isGeneral',
        render: (value: boolean) => (value ? <Text>Genel</Text> : <Text>Özel</Text>),
      },
      {
        title: 'Kullanıcı',
        dataIndex: 'account',
        key: 'account',
        render: (_: any, record: INonconformityReason) => {
          const accountId = record.accountId ?? record.account?.id;
          if (!accountId) {
            return '-';
          }
          const fullName = `${record.account?.firstName ?? ''} ${record.account?.lastName ?? ''}`.trim();
          if (fullName) {
            return fullName;
          }
          return accountLabelById.get(accountId) || record.account?.email || `#${accountId}`;
        },
      },
      {
        title: 'Eklenme Tarihi',
        dataIndex: 'createdAt',
        key: 'createdAt',
        sorter: true,
        sortOrder: sortField === 'createdAt' ? sortOrder : null,
        render: (value: string) => (value ? formatDate(value) : '-'),
      },
      {
        title: 'Güncelleme Tarihi',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        sorter: true,
        sortOrder: sortField === 'updatedAt' ? sortOrder : null,
        render: (value: string) => (value ? formatDate(value) : '-'),
      },
      {
        title: '',
        key: 'actions',
        align: 'right' as const,
        render: (_: any, record: INonconformityReason) => {
          return (
            <Space size={12}>
              <Tooltip title="Düzenle">
                <span>
                  <Button
                    shape="circle"
                    className="icon-button"
                    onClick={() => openEditDrawer(record)}
                    icon={<img src={PenIcon} alt="pen" className="icon" />}
                  />
                </span>
              </Tooltip>
              <Popconfirm
                title="Bu uygunsuzluk nedenini silmek istediğinize emin misiniz?"
                onConfirm={() => handleDelete(record)}
                okText="Evet"
                cancelText="Hayır"
              >
                <Tooltip title="Sil">
                  <span>
                    <Button
                      shape="circle"
                      className="icon-button ml-12px"
                      icon={<img src={TrashIcon} alt="trash" className="icon" />}
                    />
                  </span>
                </Tooltip>
              </Popconfirm>
            </Space>
          );
        },
      },
    ],
    [accountLabelById, sortField, sortOrder],
  );

  const applyFilterValues = (values: any) => {
    const toRangeFilter = (range?: Dayjs[]) => {
      if (!Array.isArray(range) || range.length !== 2 || !range[0] || !range[1]) {
        return undefined;
      }
      return [
        range[0].startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
        range[1].endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
      ] as [string, string];
    };
    setScopeFilter(values.scopeFilter ?? 'all');
    setAttributeFilterId(values.attributeFilterId ?? undefined);
    setAccountFilterId(values.accountIdFilter ?? undefined);
    setCreatedAtRangeFilter(toRangeFilter(values.createdAtRange));
    setUpdatedAtRangeFilter(toRangeFilter(values.updatedAtRange));
    setFilterDrawerVisible(false);
  };

  const resetFilters = () => {
    filterForm.setFieldsValue({
      scopeFilter: 'all',
      attributeFilterId: undefined,
      accountIdFilter: undefined,
      createdAtRange: undefined,
      updatedAtRange: undefined,
    });
    setScopeFilter('all');
    setAttributeFilterId(undefined);
    setAccountFilterId(undefined);
    setCreatedAtRangeFilter(undefined);
    setUpdatedAtRangeFilter(undefined);
    setFilterDrawerVisible(false);
  };

  return (
    <Card className="br-30px">
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
          <Row justify="space-between" align="middle" className="mb-16px">
            <div>
              <Title style={{ margin: 0, display: 'inline-block' }} level={4}>
                Uygunsuzluk Nedenleri
              </Title>
              <Text type="secondary" className="ms-5">
                <strong>({pagination.total})</strong> kayıt listelendi
              </Text>
            </div>
            <Row align="middle">
              <Input
                allowClear
                placeholder="Ara"
                prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
                className="d-input d-input-form d-input-filter ml-16px"
                style={{ width: 220 }}
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
              />
              <Button
                type="primary"
                className="d-button width-auto ml-10px dark-button"
                onClick={() => {
                  filterForm.setFieldsValue({
                    scopeFilter,
                    attributeFilterId,
                    accountIdFilter: accountFilterId,
                    createdAtRange: createdAtRangeFilter
                      ? [dayjs(createdAtRangeFilter[0]), dayjs(createdAtRangeFilter[1])]
                      : undefined,
                    updatedAtRange: updatedAtRangeFilter
                      ? [dayjs(updatedAtRangeFilter[0]), dayjs(updatedAtRangeFilter[1])]
                      : undefined,
                  });
                  setFilterDrawerVisible(true);
                }}
              >
                Filtrele
              </Button>
              <Button
                type="primary"
                className="d-button width-auto ml-10px dark-button"
                onClick={openCreateDrawer}
              >
                Yeni Uygunsuzluk Nedeni Ekle
              </Button>
            </Row>
          </Row>
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={loading}
          onChange={handleTableChange}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100'],
          }}
        />
      </Space>

      <Drawer
        title="Filtre"
        open={filterDrawerVisible}
        onClose={() => setFilterDrawerVisible(false)}
        destroyOnClose
        width={420}
      >
        <Form
          layout="vertical"
          form={filterForm}
          initialValues={{
            scopeFilter: 'all',
          }}
          onFinish={applyFilterValues}
        >
          <Form.Item name="scopeFilter" label="Kapsam">
            <Select>
              <Option value="all">Hepsi</Option>
              <Option value="general">Genel</Option>
              <Option value="private">Özel</Option>
            </Select>
          </Form.Item>
          <Form.Item name="attributeFilterId" label="Parametre">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              filterOption={filterOptionByTurkishLabel}
              options={activeAttributeOptions.map((item) => ({
                value: item.id,
                label: item.name,
              }))}
            />
          </Form.Item>
          <Form.Item name="accountIdFilter" label="Kullanıcı">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              filterOption={filterOptionByTurkishLabel}
              options={userSelectOptions}
            />
          </Form.Item>
          <Form.Item name="createdAtRange" label="Eklenme Tarihi Aralığı">
            <RangePicker style={{ width: '100%' }} format="DD/MM(YYYY" />
          </Form.Item>
          <Form.Item name="updatedAtRange" label="Güncelleme Tarihi Aralığı">
            <RangePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={resetFilters}>Temizle</Button>
            <Button type="primary" htmlType="submit">
              Uygula
            </Button>
          </Space>
        </Form>
      </Drawer>

      <Drawer
        title={editingItem ? 'Uygunsuzluk Nedeni Düzenle' : 'Yeni Uygunsuzluk Nedeni'}
        open={editorDrawerVisible}
        onClose={closeEditorDrawer}
        width={460}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            isGeneral: true,
          }}
        >
          <Form.Item
            name="attributeId"
            label="Parametre"
            rules={[{ required: true, message: 'Parametre seçiniz.' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              filterOption={filterOptionByTurkishLabel}
              options={editorAttributeSelectOptions.map((item) => ({ value: item.id, label: item.name }))}
            />
          </Form.Item>
          <Form.Item
            name="abbreviation"
            label="Kısaltma"
            rules={[{ required: true, message: 'Kısaltma zorunludur.' }]}
          >
            <Input maxLength={64} />
          </Form.Item>
          <Form.Item
            name="description"
            label="Açıklama"
            rules={[{ required: true, message: 'Açıklama zorunludur.' }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item
            name="isGeneral"
            label="Kapsam"
            rules={[{ required: true, message: 'Kapsam seçiniz.' }]}
          >
            <Select>
              <Option value={true}>Genel</Option>
              <Option value={false}>Özel</Option>
            </Select>
          </Form.Item>
          <Form.Item name="accountId" label="Kullanıcı (zorunlu değil)">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              filterOption={filterOptionByTurkishLabel}
              options={editorUserSelectOptions}
            />
          </Form.Item>
          <Form.Item>
            <Button block type="primary" htmlType="submit" loading={submitLoading}>
              {editingItem ? 'Güncelle' : 'Kaydet'}
            </Button>
          </Form.Item>
        </Form>
      </Drawer>
    </Card>
  );
};

export default NonconformityReasonList;
